import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import { checkStripeStatus, refreshUsage } from "@/lib/subscription";

// Post-Stripe-Checkout return page. Polls the backend status endpoint
// which itself has a Stripe fallback for late webhooks. On `paid`, we
// refresh usage so the newly-granted credits appear immediately.
const MAX_POLLS = 10;
const POLL_MS = 2000;

export function StripeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({ status: "polling", credits: 0 });

  const poll = useCallback(async (attempt) => {
    if (!sessionId) return setState({ status: "unknown", credits: 0 });
    const res = await checkStripeStatus(sessionId);
    if (res?.paymentStatus === "paid") {
      await refreshUsage().catch(() => {});
      return setState({ status: "paid", credits: res.creditsGranted || 0 });
    }
    if (res?.paymentStatus === "failed" || res?.paymentStatus === "expired") {
      return setState({ status: res.paymentStatus, credits: 0 });
    }
    if (attempt >= MAX_POLLS) return setState({ status: "timeout", credits: 0 });
    setTimeout(() => poll(attempt + 1), POLL_MS);
  }, [sessionId]);

  useEffect(() => { poll(1); }, [poll]);

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <Header />
      <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="stripe-success-page">
        {state.status === "polling" && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-[#2563eb]" />
            <h1 className="font-display text-3xl font-extrabold text-[#0f172a]">Confirming your payment…</h1>
            <p className="text-sm text-[#64748b]">This usually takes a couple of seconds.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div className="flex flex-col items-center gap-4" data-testid="stripe-success-paid">
            <CheckCircle2 className="w-14 h-14 text-emerald-500" />
            <h1 className="font-display text-3xl font-extrabold text-[#0f172a]">Payment successful — credits added</h1>
            <p className="text-sm text-[#64748b]">We just topped up your account. Head back to the studio to pick a new grooming style.</p>
            <Link to="/studio" className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#0f172a] text-white px-6 py-3 font-bold hover:bg-[#1e293b]" data-testid="stripe-success-continue-btn">
              Continue to Studio <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
        {(state.status === "failed" || state.status === "expired") && (
          <div className="flex flex-col items-center gap-4">
            <XCircle className="w-14 h-14 text-[#c84125]" />
            <h1 className="font-display text-3xl font-extrabold text-[#0f172a]">Payment did not complete</h1>
            <p className="text-sm text-[#64748b]">Nothing was charged — feel free to try again.</p>
            <Link to="/#pricing" className="text-[#2563eb] font-semibold">Back to pricing</Link>
          </div>
        )}
        {state.status === "timeout" && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 text-[#64748b]" />
            <h1 className="font-display text-3xl font-extrabold text-[#0f172a]">Still confirming…</h1>
            <p className="text-sm text-[#64748b]">Refresh in a minute — your credits will appear once Stripe confirms.</p>
          </div>
        )}
        {state.status === "unknown" && (
          <div className="flex flex-col items-center gap-4">
            <XCircle className="w-14 h-14 text-[#c84125]" />
            <h1 className="font-display text-3xl font-extrabold text-[#0f172a]">Missing session id</h1>
            <Link to="/#pricing" className="text-[#2563eb] font-semibold">Back to pricing</Link>
          </div>
        )}
      </main>
    </div>
  );
}

export function StripeCancel() {
  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <Header />
      <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="stripe-cancel-page">
        <XCircle className="w-14 h-14 text-[#64748b] mx-auto" />
        <h1 className="mt-4 font-display text-3xl font-extrabold text-[#0f172a]">Checkout cancelled</h1>
        <p className="mt-2 text-sm text-[#64748b]">No charge was made. You can pick a pack again anytime.</p>
        <Link to="/#pricing" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0f172a] text-white px-6 py-3 font-bold hover:bg-[#1e293b]" data-testid="stripe-cancel-back-btn">
          Back to pricing <ArrowRight className="w-4 h-4" />
        </Link>
      </main>
    </div>
  );
}
